import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../../axiosInstance';
import { toast } from '../../utils/toast';
import swal from 'sweetalert';
import { 
    FaArrowLeft, 
    FaPrint, 
    FaDownload, 
    FaEnvelope, 
    FaFlask, 
    FaUserMd, 
    FaCheckCircle,
    FaSpinner,
    FaHospital
} from 'react-icons/fa';

export default function LabReportScreen() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const reportRef = useRef(null);

    const user = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const [report, setReport] = useState(location.state?.reportData || null);
    const [loading, setLoading] = useState(!location.state?.reportData);
    const [isEmailing, setIsEmailing] = useState(false);

    // Fetch report detail from API
    const fetchReport = async (testId) => {
        try {
            setLoading(true);
            const res = await axiosInstance.get(`/lab-results/report/${testId}?patient=${user.id || ''}`);
            if (res.data?.data?.report) {
                setReport(res.data.data.report);
            } else {
                toast.error('Report not found');
            }
        } catch (err) {
            console.error('Failed to load lab report:', err);
            toast.error(err?.response?.data?.message || 'Could not load lab report details');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchReport(id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Native browser print (pixel-perfect A4 vector output)
    const handlePrint = () => {
        window.print();
    };

    // Request result via email
    const handleEmailRequest = async () => {
        if (!report) return;

        swal({
            title: "Email Diagnostic Report?",
            text: `Send this report to ${report.patient?.email || user.email || 'your registered email'}?`,
            icon: "info",
            buttons: ["Cancel", "Yes, Send Email"],
        }).then(async (confirmed) => {
            if (confirmed) {
                try {
                    setIsEmailing(true);
                    const res = await axiosInstance.post('/lab-results/request', {
                        patient: user.id,
                        test_id: report.test_id,
                        file: report.image || 'report',
                        status: 'pending'
                    });
                    toast.success(res.data?.message || 'Report request sent, please check your email.');
                } catch (err) {
                    console.error('Email request error:', err);
                    toast.error(err?.response?.data?.message || 'Failed to request email.');
                } finally {
                    setIsEmailing(false);
                }
            }
        });
    };

    const primaryColor = report?.hospital?.primary_color || '#087F4C';

    return (
        <div className="min-h-screen bg-gray-100 text-gray-900 pb-16">
            {/* Embedded Print CSS Rules for A4 Paper formatting */}
            <style dangerouslySetInnerHTML={{
                __html: `
                @media print {
                    @page {
                        size: A4 portrait;
                        margin: 12mm 15mm 12mm 15mm;
                    }
                    html, body {
                        background: #ffffff !important;
                        color: #000000 !important;
                        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
                        font-size: 13px !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    .no-print {
                        display: none !important;
                    }
                    .report-card {
                        box-shadow: none !important;
                        border: none !important;
                        padding: 0 !important;
                        margin: 0 !important;
                        width: 100% !important;
                        max-width: 100% !important;
                    }
                    .printable-table th {
                        background-color: #f1f5f9 !important;
                        color: #000000 !important;
                        border-top: 1.5px solid #000000 !important;
                        border-bottom: 1.5px solid #000000 !important;
                    }
                    .printable-table td {
                        border-bottom: 1px solid #cbd5e1 !important;
                        color: #000000 !important;
                    }
                    .avoid-break {
                        page-break-inside: avoid !important;
                        break-inside: avoid !important;
                    }
                    tr {
                        page-break-inside: avoid !important;
                        break-inside: avoid !important;
                    }
                }
                `
            }} />

            {/* Top Toolbar (Hidden on Print) */}
            <header className="no-print sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-200 px-4 sm:px-6 py-3 shadow-xs">
                <div className="max-w-4xl mx-auto flex items-center justify-between gap-3">
                    {/* Back Button */}
                    <button
                        onClick={() => navigate('/lab_results')}
                        className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-xl text-xs sm:text-sm font-semibold transition-all active:scale-95"
                    >
                        <FaArrowLeft className="text-xs" />
                        <span>Back to Results</span>
                    </button>

                    {/* Report Status */}
                    <div className="hidden sm:flex items-center space-x-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                            <FaCheckCircle className="mr-1 text-[11px]" />
                            Verified Diagnostic Result
                        </span>
                    </div>

                    {/* Action Buttons: Print, PDF, Email */}
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={handleEmailRequest}
                            disabled={isEmailing || !report}
                            className="flex items-center space-x-1.5 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-3 py-1.5 rounded-xl text-xs sm:text-sm font-semibold transition-all active:scale-95 disabled:opacity-50"
                        >
                            {isEmailing ? <FaSpinner className="animate-spin text-xs" /> : <FaEnvelope className="text-xs text-primary100" />}
                            <span className="hidden xs:inline">Email Me</span>
                        </button>

                        <button
                            onClick={handlePrint}
                            disabled={loading || !report}
                            className="flex items-center space-x-1.5 bg-primary100 text-white hover:bg-primary100/90 px-3.5 py-1.5 rounded-xl text-xs sm:text-sm font-semibold shadow-xs transition-all active:scale-95 disabled:opacity-50"
                            style={{ backgroundColor: primaryColor }}
                        >
                            <FaPrint className="text-xs" />
                            <span>Print / PDF</span>
                        </button>
                    </div>
                </div>
            </header>

            {/* Main Content Area */}
            <main className="max-w-4xl mx-auto px-3 sm:px-6 pt-4 sm:pt-6">
                {loading && (
                    <div className="bg-white rounded-2xl p-12 text-center shadow-xs border border-gray-200 flex flex-col items-center justify-center">
                        <FaSpinner className="text-3xl text-primary100 animate-spin mb-3" style={{ color: primaryColor }} />
                        <h3 className="text-base font-semibold text-gray-800">Generating Diagnostic Report...</h3>
                        <p className="text-xs text-gray-500 mt-1">Fetching verified test parameters and clinical observations</p>
                    </div>
                )}

                {!loading && !report && (
                    <div className="bg-white rounded-2xl p-12 text-center shadow-xs border border-gray-200">
                        <FaFlask className="text-4xl text-gray-400 mx-auto mb-3" />
                        <h3 className="text-lg font-bold text-gray-800">Diagnostic Report Not Found</h3>
                        <p className="text-sm text-gray-500 mt-1 mb-5">We could not load the results for this test. Please try again.</p>
                        <button
                            onClick={() => navigate('/lab_results')}
                            className="bg-primary100 text-white px-5 py-2 rounded-xl text-xs font-semibold"
                            style={{ backgroundColor: primaryColor }}
                        >
                            Return to Lab List
                        </button>
                    </div>
                )}

                {!loading && report && (
                    <div 
                        ref={reportRef}
                        id="printableReport"
                        className="report-card bg-white rounded-2xl shadow-sm border border-gray-200 p-5 sm:p-8"
                    >
                        {/* ========================================================= */}
                        {/* 1. HOSPITAL LETTERHEAD HEADER */}
                        {/* ========================================================= */}
                        <div className="flex flex-col sm:flex-row items-center justify-between border-b-2 pb-4 mb-4" style={{ borderColor: primaryColor }}>
                            <div className="text-center sm:text-left mb-3 sm:mb-0">
                                <h1 
                                    className="text-xl sm:text-2xl font-black tracking-tight"
                                    style={{ color: primaryColor }}
                                >
                                    {report.hospital?.name || 'Medical Centre'}
                                </h1>
                                {report.hospital?.address && (
                                    <p className="text-xs text-gray-600 mt-0.5 max-w-md font-medium leading-relaxed">
                                        {report.hospital.address}
                                    </p>
                                )}
                                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-3 text-[11px] text-gray-500 mt-1 font-medium">
                                    {report.hospital?.phone && (
                                        <span>Tel: <strong>{report.hospital.phone}</strong></span>
                                    )}
                                    {report.hospital?.email && (
                                        <span>Email: <strong>{report.hospital.email}</strong></span>
                                    )}
                                    {report.hospital?.whatsapp && (
                                        <span>WhatsApp: <strong>{report.hospital.whatsapp}</strong></span>
                                    )}
                                </div>
                            </div>

                            {/* Hospital Badge or EMR URL Logo */}
                            <div className="shrink-0 flex items-center justify-center">
                                <div 
                                    className="w-14 h-14 rounded-2xl flex items-center justify-center font-black text-xl shadow-xs"
                                    style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
                                >
                                    <FaHospital className="text-2xl" />
                                </div>
                            </div>
                        </div>

                        {/* ========================================================= */}
                        {/* 2. PATIENT DEMOGRAPHICS TABLE */}
                        {/* ========================================================= */}
                        <div className="bg-gray-50 rounded-xl p-3 sm:p-4 mb-5 border border-gray-200">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                                <div className="col-span-2 sm:col-span-2">
                                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">Patient Name</span>
                                    <strong className="text-gray-900 font-black text-xs sm:text-sm block break-words leading-snug">
                                        {report.patient?.full_name || 'Patient'}
                                    </strong>
                                </div>
                                <div className="col-span-1 sm:col-span-1">
                                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">Card / File No</span>
                                    <strong className="text-gray-900 font-black text-xs sm:text-sm block break-words">
                                        {report.patient?.card_no || 'N/A'}
                                    </strong>
                                </div>
                                <div className="col-span-1 sm:col-span-1">
                                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">Age / Gender</span>
                                    <strong className="text-gray-900 font-bold text-xs block break-words">
                                        {report.patient?.age || 'N/A'} &bull; {report.patient?.gender || 'N/A'}
                                    </strong>
                                </div>
                                <div className="col-span-1 sm:col-span-1">
                                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">Specimen / Sample</span>
                                    <strong className="text-gray-900 font-bold text-xs block break-words">
                                        Blood / Serum
                                    </strong>
                                </div>

                                <div className="col-span-1 sm:col-span-1">
                                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">Date Requested</span>
                                    <span className="text-gray-800 font-semibold text-xs block">
                                        {report.created ? report.created.substring(0, 10) : 'Recent'}
                                    </span>
                                </div>
                                <div className="col-span-1 sm:col-span-1">
                                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">Date Reported</span>
                                    <span className="text-gray-800 font-semibold text-xs block">
                                        {report.reported_at ? report.reported_at.substring(0, 10) : (report.modified ? report.modified.substring(0, 10) : 'Recent')}
                                    </span>
                                </div>
                                <div className="col-span-1 sm:col-span-1">
                                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">Reported By</span>
                                    <span className="text-gray-800 font-semibold text-xs flex items-center space-x-1">
                                        <FaUserMd className="text-gray-400 text-[10px] shrink-0" />
                                        <span className="break-words">{report.reported_by_name || 'Medical Laboratory Scientist'}</span>
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* ========================================================= */}
                        {/* 3. REPORT TITLE BANNER */}
                        {/* ========================================================= */}
                        <div 
                            className="rounded-lg py-2.5 px-4 text-center text-white font-black text-sm uppercase tracking-wider shadow-xs mb-4 flex items-center justify-center"
                            style={{ backgroundColor: primaryColor }}
                        >
                            <span>LABORATORY DIAGNOSTIC REPORT</span>
                        </div>

                        {/* Test Group Title & Clinical Indication */}
                        <div className="mb-4 pb-2 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                            <div>
                                <h2 className="text-base font-black text-gray-900 uppercase tracking-tight">
                                    {report.test_name}
                                </h2>
                                {report.notes && (
                                    <p className="text-xs text-gray-500 italic mt-0.5">
                                        Clinical Indication: {report.notes}
                                    </p>
                                )}
                            </div>
                            <div className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-md self-start sm:self-auto">
                                Result Complete
                            </div>
                        </div>

                        {/* ========================================================= */}
                        {/* 4. STRUCTURED V2 FINDINGS TABLE */}
                        {/* ========================================================= */}
                        {report.has_v2_results && report.v2_results?.length > 0 ? (
                            <div className="overflow-x-auto mb-6">
                                <table className="printable-table w-full text-left border-collapse text-xs">
                                    <thead>
                                        <tr className="border-t-2 border-b-2 border-gray-900 bg-gray-50">
                                            <th className="py-2.5 px-3 font-black text-gray-900 uppercase tracking-wider w-[42%]">
                                                Investigation / Parameter
                                            </th>
                                            <th className="py-2.5 px-3 font-black text-gray-900 uppercase tracking-wider w-[22%]">
                                                Result
                                            </th>
                                            <th className="py-2.5 px-3 font-black text-gray-900 uppercase tracking-wider w-[14%]">
                                                Flag
                                            </th>
                                            <th className="py-2.5 px-3 font-black text-gray-900 uppercase tracking-wider w-[22%]">
                                                Ref. Value(s)
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 font-medium">
                                        {report.v2_results.map((row, idx) => {
                                            const flag = (row.flag || '').toLowerCase();
                                            const isHigh = flag === 'high';
                                            const isLow = flag === 'low';

                                            return (
                                                <tr key={idx} className="hover:bg-gray-50/80 transition-colors">
                                                    <td className="py-2 px-3 font-bold text-gray-900">
                                                        {row.solution}
                                                    </td>
                                                    <td className="py-2 px-3 font-black text-gray-900 text-xs sm:text-sm">
                                                        {row.result}
                                                    </td>
                                                    <td className="py-2 px-3">
                                                        {isHigh ? (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-black bg-red-100 text-red-700 border border-red-200">
                                                                HIGH
                                                            </span>
                                                        ) : isLow ? (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-black bg-blue-100 text-blue-700 border border-blue-200">
                                                                LOW
                                                            </span>
                                                        ) : (
                                                            <span className="text-gray-500 font-semibold text-[11px]">
                                                                {row.flag || 'Normal'}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="py-2 px-3 text-gray-600 font-semibold">
                                                        {row.reference || '-'}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            /* Fallback for V1 legacy file or image */
                            <div className="mb-6">
                                {report.image_url ? (
                                    <div className="text-center p-4 bg-gray-50 rounded-xl border border-gray-200">
                                        <p className="text-xs text-gray-500 font-semibold mb-3">Scanned Diagnostic Document</p>
                                        <img
                                            src={report.image_url}
                                            alt={report.test_name}
                                            className="max-h-[600px] w-auto mx-auto rounded-lg shadow-sm border border-gray-200 object-contain"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = "https://via.placeholder.com/700x500?text=Document+Preview+Unavailable";
                                            }}
                                        />
                                        <div className="mt-4 no-print">
                                            <a
                                                href={report.image_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center space-x-1.5 bg-primary100 text-white px-4 py-2 rounded-xl text-xs font-semibold shadow-xs"
                                                style={{ backgroundColor: primaryColor }}
                                            >
                                                <FaDownload className="text-xs" />
                                                <span>Download Original Image</span>
                                            </a>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="p-8 text-center bg-gray-50 rounded-xl border border-gray-200 text-gray-500 text-xs">
                                        No digital parameter findings or scan file attached for this record.
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ========================================================= */}
                        {/* 5. CLINICAL COMMENTS SECTION */}
                        {/* ========================================================= */}
                        {report.clinical_comment && (
                            <div className="avoid-break mb-6 p-4 rounded-xl bg-gray-50 border border-gray-300">
                                <span className="text-[10px] text-gray-500 font-black uppercase tracking-wider block mb-1">
                                    Clinical Comment & Impression
                                </span>
                                <p className="text-xs text-gray-900 font-bold whitespace-pre-line leading-relaxed">
                                    {report.clinical_comment}
                                </p>
                            </div>
                        )}

                        {/* ========================================================= */}
                        {/* 6. SIGNOFF & AUTHENTICATION FOOTER */}
                        {/* ========================================================= */}
                        <div className="avoid-break pt-4 border-t-2 border-gray-900 flex flex-col sm:flex-row items-center justify-between text-[11px] text-gray-500 gap-2">
                            <div>
                                <p className="font-semibold text-gray-700">
                                    Reported & Verified: <strong>{report.reported_by_name || 'Medical Laboratory'}</strong>
                                </p>
                                <p className="text-[10px] text-gray-400">
                                    Generated via Misas Health Portal &bull; Electronically Authenticated
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}
